import { DEFAULT_PALETTE } from './peacockPalette';

const AAA_CONTRAST_FLOOR = 7;
const LIGHT_SURFACE = DEFAULT_PALETTE.canvas_light;
const DARK_SURFACE = DEFAULT_PALETTE.canvas_dark;
const VALID_THEME_MODES = new Set(['light', 'dark', 'custom', 'auto']);

export function normalizeHex(hex) {
    const value = String(hex || '').trim().replace(/^#+/, '');
    if (/^[0-9a-fA-F]{6}$/.test(value)) return `#${value.toUpperCase()}`;
    if (/^[0-9a-fA-F]{3}$/.test(value)) {
        return `#${value.split('').map((c) => c + c).join('').toUpperCase()}`;
    }
    return null;
}

function hexToRgb(hex) {
    const normalized = normalizeHex(hex);
    if (!normalized) return null;
    const int = parseInt(normalized.slice(1), 16);
    return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255
    };
}

function rgbToHex({ r, g, b }) {
    return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

function srgbToLinear(channel) {
    const value = channel / 255;
    return value <= 0.04045
        ? value / 12.92
        : ((value + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    return (0.2126 * srgbToLinear(rgb.r)) +
        (0.7152 * srgbToLinear(rgb.g)) +
        (0.0722 * srgbToLinear(rgb.b));
}

export function contrastRatio(foregroundHex, surfaceHex) {
    const fg = relativeLuminance(foregroundHex);
    const bg = relativeLuminance(surfaceHex);
    if (fg == null || bg == null) return null;
    const lighter = Math.max(fg, bg);
    const darker = Math.min(fg, bg);
    return (lighter + 0.05) / (darker + 0.05);
}

function mixRgb(from, to, weight) {
    return {
        r: Math.round(from.r + (to.r - from.r) * weight),
        g: Math.round(from.g + (to.g - from.g) * weight),
        b: Math.round(from.b + (to.b - from.b) * weight)
    };
}

export function computeAAAVariant(hex, surfaceHex) {
    const baseHex = normalizeHex(hex);
    const backgroundHex = normalizeHex(surfaceHex);
    if (!baseHex || !backgroundHex) return baseHex || backgroundHex || null;

    const startingContrast = contrastRatio(baseHex, backgroundHex);
    if (startingContrast != null && startingContrast >= AAA_CONTRAST_FLOOR) {
        return baseHex;
    }

    const baseRgb = hexToRgb(baseHex);
    const surfaceLum = relativeLuminance(backgroundHex);
    const target = surfaceLum != null && surfaceLum > 0.179 ? 0 : 255;

    let low = 0;
    let high = 1;
    let best = null;

    for (let i = 0; i < 24; i += 1) {
        const weight = (low + high) / 2;
        const mixed = mixRgb(baseRgb, { r: target, g: target, b: target }, weight);
        const candidateHex = rgbToHex(mixed);
        const candidateContrast = contrastRatio(candidateHex, backgroundHex);

        if (candidateContrast != null && candidateContrast >= AAA_CONTRAST_FLOOR) {
            best = candidateHex;
            high = weight;
        } else {
            low = weight;
        }
    }

    if (best) return best;

    return rgbToHex(mixRgb(baseRgb, { r: target, g: target, b: target }, 1));
}

export function resolveActiveBrand(brands = [], activeBrandId = '') {
    if (!Array.isArray(brands) || !activeBrandId) return null;
    return brands.find((brand) => brand.id === activeBrandId) || null;
}

export function resolveEntityDefaultThemeMode(activeBrand) {
    const themeMode = activeBrand?.custom?.default_theme_mode;
    return VALID_THEME_MODES.has(themeMode) ? themeMode : null;
}

export function resolveEntityDefaultThemeSettings(currentSettings, activeBrand) {
    if (!currentSettings || currentSettings.themeExplicitlySet) return currentSettings;

    const defaultThemeMode = resolveEntityDefaultThemeMode(activeBrand);
    if (!defaultThemeMode || currentSettings.theme === defaultThemeMode) return currentSettings;

    return { ...currentSettings, theme: defaultThemeMode };
}

export function resolveActiveSurfaceHex({ theme = 'light', customTheme, activeBrand } = {}) {
    const normalizedCustomSurface = normalizeHex(customTheme?.surface);

    if (theme === 'custom') {
        return normalizedCustomSurface || LIGHT_SURFACE;
    }

    const paletteSurfaceHex = theme === 'dark'
        ? activeBrand?.color_palette?.canvas_dark
        : activeBrand?.color_palette?.canvas_light;
    const normalizedPaletteSurface = normalizeHex(paletteSurfaceHex);

    return normalizedPaletteSurface || (theme === 'dark' ? DARK_SURFACE : LIGHT_SURFACE);
}

export function resolvePaletteTextHex(hex, options = {}) {
    const surfaceHex = resolveActiveSurfaceHex(options);
    return computeAAAVariant(hex, surfaceHex);
}
